import React, { useState, useEffect } from "react";
import { Button } from "react-bootstrap";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/all";

import styles from "./moonshot.module.scss";

const Moonshot = () => {
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const smallDevice = windowWidth <= 990;
  const largeDevice = windowWidth > 990;

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener("resize", handleResize);
    setWindowWidth(window.innerWidth);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    gsap.registerPlugin(ScrollTrigger);

    const mainTimeline = gsap.timeline({
      scrollTrigger: {
        trigger: "#moonshotWrapper",
        scrub: true,
        end: "+=750",
      },
    });

    const moonTimeline = gsap.timeline();
    moonTimeline.from("#moon", {
      y: 350,
      duration: 90,
      opacity: 0.8,
      delay: 4,
    });

    const textTimeline = gsap.timeline();
    textTimeline.from("#textContent", {
      y: 500,
      duration: 95,
      opacity: 0.5,
      delay: 5,
    });

    mainTimeline.add(moonTimeline, 12);
    mainTimeline.add(textTimeline, 24);
  }, []);

  return (
    <section className={styles.moonshotWrapper} id="moonshotWrapper">
      <div className={styles.sceneSection}>
        <div className={styles.moonWrapper}>
          <img
            className={styles.moon}
            id="moon"
            src={"/images/moonshot/moon.png"}
            alt=""
          />
          <div className={styles.textContent} id="textContent">
            <p className={styles.theMoonshot}>
              <span>The</span>
              <span>Moonshot!</span>
            </p>
            <p className={styles.staffFavorite}>Our Staff Favorite</p>
            <a href="https://literaturecorner.perfectionlearning.com/" target="_blank">
              <img
                className={styles.moonshotLogo}
                src={"/images/moonshot/perfectionLogo.png"}
              />
            </a>
          </div>
        </div>
        <img
          className={styles.terrainImg}
          src={
            largeDevice
              ? "/images/moonshot/terrain_desktop.png"
              : "/images/moonshot/terrain_mobile.png"
          }
        />
        <img
          className={styles.astronaut}
          src={"/images/moonshot/astronaut.png"}
          alt=""
        />
      </div>

      <div className={styles.contentSection}>
        <p className={styles.timeline}>MAY, 2026</p>
        <div className={styles.headingSection}>
          It was the best of content, it was the silence of discoverability. <br />
          <span className={styles.subHeader}>
            With apologies to Charles Dickens, A Tale of Two Cities, 1859
          </span>
        </div>
        <div className={styles.imageTextWrapper}></div>

        <div className={styles.imageSection}>
          <a href="https://literaturecorner.perfectionlearning.com/" target="_blank">
            <div className={styles.videoContainer}>
              <img
                className={styles.frameBackground}
                src={largeDevice ? "/images/moonshot/perfectionLearning.png" : "/images/moonshot/perfectionMobile.png"}
                alt=""
              />
              <video
                className={styles.websiteImg}
                src={largeDevice ? "/images/moonshot/perfectionDesktop.webm" : "/images/moonshot/perfectionMobile.webm"}
                autoPlay
                loop
                muted
                playsInline
              />
            </div>
          </a>
          <img
            className={styles.builtWithCleverstory}
            src={"/images/moonshot/builtWithCleverstory.png"}
            alt=""
          />
        </div>

        <div className={styles.textSection}>
          <div className={styles.QnA}>
            <div className={styles.leftPassage}>
              <span className={styles.paragraph}>
                Dickens was writing about revolution. But the tension he named greatness existing alongside an equal and opposite failure to reach the people who need it describes educational publishing more precisely than most industry reports do.
              </span>

              <span className={styles.paragraph}>
                Perfection Learning has spent over a hundred years building literature guides, ELA resources, and classroom materials that teachers trust. The content was never the problem. The problem was that their catalog had no front door. A teacher searching on a Sunday night might find the right guide. Might not. Might settle for something worse from somewhere else and never know what they missed.
              </span>

              <span className={styles.paragraph}>
                They came to Cleverstory with a simple brief: one hub, their own domain, everything in one place. Free. No login. No form. No wall between a teacher and what they were looking for.
              </span>

              <span className={styles.paragraph}>
                The result was The Literature Corner, a branded resource hub at literaturecorner.perfectionlearning.com, housing their full catalog of free ELA materials. Built by their marketing team. No dev resources. No six-month build.
              </span>

              <span className={styles.paragraph}>
                Every resource is filterable by grade. Searchable from the first click. A teacher who arrived looking for something specific could find it in seconds. One who was browsing could follow the content wherever it took them.
              </span>
            </div>
            <div className={styles.rightPassage}>

              <span className={styles.paragraph}>
                Cleverstory's analytics showed exactly who was arriving, what they were spending time on, and what they kept coming back for.
              </span>

              <span className={styles.paragraph}>
                Teachers arrived from California, Iowa, New York, Texas, North Carolina. Through email campaigns, Google ads, and links sent directly by sales reps. Coming for AP Language graphic organizers, memoir guides, poetry resources, and literature activities for books their students had strong opinions about.
              </span>

              <span className={styles.paragraph}>
                AI gating meant a well-timed ask could arrive after value was already delivered, not before it.
              </span>

              <span className={styles.paragraph}>
                <span className={styles.boldText}>13,600+</span> <span className={styles.semiBoldText}>sessions.</span> And for the first time, Perfection Learning could see which resources were landing, which were being returned to, and which were still waiting to be discovered.
                It was the best of content. Now it was finally the best of discoverability too.
              </span>
              <div className={styles.liveExperienceLink}>
                <a href="https://literaturecorner.perfectionlearning.com/" target="_blank">
                  Live Experience
                </a>
              </div>
            </div>

            <img
              className={styles.astronautImage}
              src={"/images/astronaut.png"}
              alt=""
            />

          </div>
        </div>
      </div>
    </section>
  );
};

export default Moonshot;
